/**
 * Main Layout Component
 * Primary layout wrapper with navigation and footer
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation } from 'react-router-dom';
import { Navigation } from './Navigation';
import { Footer } from './Footer';
import { useSafeThemeClasses, useSafeTheme } from '@/shared/theme';
import { trackPageView } from '@/shared/utils/pageViewTracker';
import FantasyEmberAsh from '@/shared/theme/fantasy/FantasyEmberAsh';

interface MainLayoutProps {
  children?: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useSafeThemeClasses();
  const { variant } = useSafeTheme();
  const location = useLocation();
  
  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);
  
  const isHome = location.pathname === '/';

  return (
    <div
      className={`min-h-screen flex flex-col glass-container relative ${
        isHome ? theme.layoutShellHome : theme.background
      }`}
    >
      {/* Fixed orbs — hidden on home so Welcome's fixed video/gradient is the
          only full-viewport bg. Portaled to <body>: this div's own would-be
          parent (theme.background, right above) carries backdrop-blur-sm,
          which establishes a new containing block for position:fixed
          descendants per the CSS spec - the same trap already fixed for the
          admin sidebar and the fantasy ambient layer. Left in place, "fixed
          inset-0" resolved against that ancestor's padding box instead of
          the viewport, so on any page taller than one screen the box grew
          to match the page's full scrollable height instead of staying
          pinned to the viewport - the orbs drifted with scroll instead of
          staying put, and inflated the document's reported scroll height
          with off-screen, clipped content. */}
      {!isHome ? createPortal(
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse opacity-25`}></div>
          <div
            className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse opacity-25`}
            style={{ animationDelay: '1s' }}
          ></div>
          <div
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-3xl ${theme.orbs[0]} opacity-20 animate-pulse`}
            style={{ animationDelay: '2s' }}
          ></div>
        </div>,
        document.body
      ) : null}

      {!isHome && variant === 'fantasy' ? <FantasyEmberAsh /> : null}

      <Navigation />
      
      {/* Main content area */}
      <main className="flex-1 relative z-10">
        {children || <Outlet />}
      </main>
      
      <Footer />
    </div>
  );
};

export default MainLayout;
